import { useEffect, useRef, useState } from "react";
import {
  ShieldCheck,
  Mail,
  Sparkles,
  Users,
  MessageSquare,
  UserPlus,
  Send,
  KeyRound,
  Copy,
  CheckCircle2,
  AlertTriangle,
  RefreshCw,
  Upload,
  Loader2,
  Shield,
  Search,
  Activity,
} from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { fmtDate, fmtMonth, fmtNum } from "@/lib/format";
import { SectionCard, KpiCard, InfoNote, Row, Segmented } from "@/components/common/Cards";
import { Field, MoneyInput, NumInput, TextInput } from "@/components/common/Inputs";
import { Sel, SwitchRow } from "@/components/common/Sel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ResourcePanel } from "@/components/admin/ResourcePanel";

export default function AdminPage() {
  return (
    <div className="max-w-[1400px] mx-auto space-y-6">
      <div>
        <div className="eyebrow flex items-center gap-2">
          <ShieldCheck className="h-3.5 w-3.5" /> Administrator
        </div>
        <h1 className="font-display text-4xl mt-1.5" data-testid="admin-page-title">
          Pengaturan Admin
        </h1>
        <p className="text-sm text-muted-foreground mt-1.5 max-w-2xl leading-relaxed">
          Kelola akses pengguna, konfigurasi Email, WhatsApp, AI (termasuk kuota token), serta data inventory server.
        </p>
      </div>

      <Tabs defaultValue="users">
        <TabsList className="bg-[hsl(220_12%_8%)] border border-border h-auto p-1 flex-wrap" data-testid="admin-tabs">
          <TabsTrigger value="users" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-4 py-2" data-testid="admin-tab-users">
            <Users className="h-4 w-4 mr-1.5" /> Pengguna & Kuota
          </TabsTrigger>
          <TabsTrigger value="email" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-4 py-2" data-testid="admin-tab-email">
            <Mail className="h-4 w-4 mr-1.5" /> Email
          </TabsTrigger>
          <TabsTrigger value="whatsapp" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-4 py-2" data-testid="admin-tab-whatsapp">
            <MessageSquare className="h-4 w-4 mr-1.5" /> WhatsApp
          </TabsTrigger>
          <TabsTrigger value="ai" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-4 py-2" data-testid="admin-tab-ai">
            <Sparkles className="h-4 w-4 mr-1.5" /> AI & Kuota
          </TabsTrigger>
          <TabsTrigger value="inventory" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-4 py-2" data-testid="admin-tab-inventory">
            <RefreshCw className="h-4 w-4 mr-1.5" /> Inventory
          </TabsTrigger>
          <TabsTrigger value="scraping" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-4 py-2" data-testid="admin-tab-scraping">
            <Search className="h-4 w-4 mr-1.5" /> Scraping
          </TabsTrigger>
          <TabsTrigger value="resources" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-4 py-2" data-testid="admin-tab-resources">
            <Activity className="h-4 w-4 mr-1.5" /> Resource
          </TabsTrigger>
          <TabsTrigger value="security" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-4 py-2" data-testid="admin-tab-security">
            <Shield className="h-4 w-4 mr-1.5" /> Keamanan
          </TabsTrigger>
        </TabsList>

        <TabsContent value="users" className="mt-5">
          <UsersPanel />
        </TabsContent>
        <TabsContent value="email" className="mt-5">
          <SettingsPanel section="email" />
        </TabsContent>
        <TabsContent value="whatsapp" className="mt-5">
          <SettingsPanel section="whatsapp" />
        </TabsContent>
        <TabsContent value="ai" className="mt-5">
          <SettingsPanel section="ai" />
        </TabsContent>
        <TabsContent value="inventory" className="mt-5">
          <InventoryPanel />
        </TabsContent>
        <TabsContent value="scraping" className="mt-5">
          <SettingsPanel section="scraping" />
        </TabsContent>
        <TabsContent value="resources" className="mt-5">
          <ResourcePanel />
        </TabsContent>
        <TabsContent value="security" className="mt-5">
          <SettingsPanel section="security" />
        </TabsContent>
      </Tabs>
    </div>
  );
}

/* ------------------------- Users / Quota ------------------------- */
function UsersPanel() {
  const [report, setReport] = useState(null);
  const [users, setUsers] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [pinDialog, setPinDialog] = useState(null);
  const [editLimit, setEditLimit] = useState(null);
  const load = async () => {
    try {
      const [r1, r2] = await Promise.all([api.get("/admin/usage"), api.get("/admin/users")]);
      setReport(r1.data);
      setUsers(r2.data);
    } catch (e) {
      toast.error(errMsg(e, "Gagal memuat"));
    }
  };
  useEffect(() => {
    load();
  }, []);

  if (!report || !users) {
    return (
      <div className="space-y-3" data-testid="admin-users-loading">
        <Skeleton className="h-24 bg-[hsl(220_10%_11%)]" />
        <Skeleton className="h-64 bg-[hsl(220_10%_11%)]" />
      </div>
    );
  }

  const topTool = report.by_tool?.[0];

  return (
    <div className="space-y-5">
      <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-3">
        <KpiCard label="Total token bulan ini" value={fmtNum(report.total_tokens)} sub={fmtMonth(report.month)} icon={Sparkles} accent testid="admin-kpi-total-tokens" />
        <KpiCard label="Total request" value={fmtNum(report.total_requests)} sub={`${users.users.length} pengguna aktif`} icon={Sparkles} testid="admin-kpi-requests" />
        <KpiCard label="Kuota default" value={`${fmtNum(report.default_limit)}`} sub="Token / user / bulan" testid="admin-kpi-default-limit" />
        <KpiCard label="Tool terbanyak" value={topTool ? topTool.tool : "–"} sub={topTool ? `${fmtNum(topTool.tokens)} token` : "–"} testid="admin-kpi-top-tool" />
      </div>

      <SectionCard
        title="Pengguna & kuota token"
        description="Atur batas token per pengguna (default 500.000/bulan). Buat PIN manual untuk pengguna yang tidak menerima email OTP."
        actions={
          <Button size="sm" onClick={() => setAddOpen(true)} data-testid="admin-add-user-button">
            <UserPlus className="h-4 w-4" /> Tambah pengguna
          </Button>
        }
        bodyClassName="p-0"
        testid="admin-users-card"
      >
        <div className="overflow-x-auto scroll-gold">
          <table className="dense-table w-full">
            <thead>
              <tr className="bg-secondary">
                <th className="text-left px-5 py-3">Email</th>
                <th className="text-left px-3 py-3">Peran</th>
                <th className="text-left px-3 py-3">Status</th>
                <th className="text-right px-3 py-3">Kuota</th>
                <th className="text-right px-3 py-3">Terpakai</th>
                <th className="text-right px-3 py-3">Request</th>
                <th className="text-right px-3 py-3">Perhitungan</th>
                <th className="px-5 py-3" />
              </tr>
            </thead>
            <tbody>
              {users.users.map((u) => {
                const pct = u.effective_limit ? (u.used / u.effective_limit) * 100 : 0;
                return (
                  <tr key={u.email} className="border-t border-border hover:bg-[hsl(220_10%_9%)]" data-testid={`admin-user-row-${u.email}`}>
                    <td className="px-5 py-3">
                      <div className="font-medium">{u.email}</div>
                      {u.display_name && <div className="text-[11px] text-muted-foreground">{u.display_name}</div>}
                      {u.last_login_at && <div className="text-[11px] text-muted-foreground">Login terakhir {fmtDate(u.last_login_at)}</div>}
                    </td>
                    <td className="px-3 py-3">
                      <Badge variant="outline" className={`border-border text-[10px] ${u.role === "admin" ? "text-primary" : "text-muted-foreground"}`}>
                        {u.role}
                      </Badge>
                    </td>
                    <td className="px-3 py-3">
                      <Badge variant="outline" className={`border-border text-[10px] ${u.status === "inactive" ? "text-[hsl(0_72%_64%)]" : "text-[hsl(160_55%_60%)]"}`}>
                        {u.status || "active"}
                      </Badge>
                    </td>
                    <td className="px-3 py-3 text-right num">
                      {fmtNum(u.effective_limit)}
                      {u.token_limit !== null && u.token_limit !== undefined && (
                        <span className="block text-[10px] text-[hsl(43_60%_66%)]">khusus</span>
                      )}
                    </td>
                    <td className="px-3 py-3 text-right">
                      <div className="num">{fmtNum(u.used)}</div>
                      <Progress value={Math.min(100, pct)} className="h-1 mt-1 bg-[hsl(220_10%_14%)] max-w-[100px] ml-auto" />
                    </td>
                    <td className="px-3 py-3 text-right num text-muted-foreground">{fmtNum(u.requests)}</td>
                    <td className="px-3 py-3 text-right num text-muted-foreground">{fmtNum(u.calculations)}</td>
                    <td className="px-5 py-3 text-right whitespace-nowrap">
                      <div className="flex justify-end gap-1">
                        <Button size="sm" variant="ghost" onClick={() => setEditLimit(u)} data-testid={`admin-user-edit-limit-${u.email}`}>
                          <Sparkles className="h-3.5 w-3.5" /> Kuota
                        </Button>
                        {u.role !== "admin" && (
                          <Button size="sm" variant="ghost" onClick={() => generatePin(u.email, setPinDialog)} data-testid={`admin-user-generate-pin-${u.email}`}>
                            <KeyRound className="h-3.5 w-3.5" /> PIN
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {users.users.length === 0 && (
                <tr>
                  <td colSpan={8} className="text-center py-10 text-sm text-muted-foreground">
                    Belum ada pengguna. Tambahkan atau minta rekan Anda sign in dengan email yang diizinkan.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </SectionCard>

      <AddUserDialog open={addOpen} onOpenChange={setAddOpen} onDone={load} />
      <EditLimitDialog user={editLimit} onOpenChange={() => setEditLimit(null)} onDone={load} defaultLimit={users.default_limit} />
      <PinDialog data={pinDialog} onOpenChange={() => setPinDialog(null)} />
    </div>
  );
}

async function generatePin(email, setPinDialog) {
  try {
    const { data } = await api.post(`/admin/users/${encodeURIComponent(email)}/generate-pin`);
    setPinDialog(data);
  } catch (e) {
    toast.error(errMsg(e, "Gagal membuat PIN"));
  }
}

function PinDialog({ data, onOpenChange }) {
  return (
    <Dialog open={!!data} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border sm:max-w-md" data-testid="admin-pin-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><KeyRound className="h-5 w-5 text-primary" /> PIN Baru</DialogTitle>
          <DialogDescription>
            Bagikan PIN ini ke <b className="text-foreground">{data?.email}</b>. Berlaku {data?.expires_in_minutes} menit.
          </DialogDescription>
        </DialogHeader>
        <div className="flex items-center justify-center p-6 rounded-xl border border-[color:var(--hairline)] bg-[hsl(43_30%_8%)]">
          <span className="num text-5xl tracking-widest text-primary" data-testid="admin-pin-value">{data?.pin}</span>
        </div>
        <DialogFooter className="flex justify-between items-center">
          <Button
            variant="secondary"
            onClick={() => {
              navigator.clipboard?.writeText(data?.pin || "");
              toast.success("PIN disalin");
            }}
            data-testid="admin-pin-copy"
          >
            <Copy className="h-4 w-4" /> Salin
          </Button>
          <Button onClick={() => onOpenChange()} data-testid="admin-pin-close">Tutup</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AddUserDialog({ open, onOpenChange, onDone }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) {
      setEmail("");
      setName("");
    }
  }, [open]);
  const save = async () => {
    const e = email.trim().toLowerCase();
    if (!e || !e.includes("@")) return toast.error("Email tidak valid");
    setSaving(true);
    try {
      await api.post("/admin/users", { email: e, display_name: name });
      toast.success("Pengguna ditambahkan");
      onOpenChange(false);
      onDone && onDone();
    } catch (err) {
      toast.error(errMsg(err, "Gagal menambah"));
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border sm:max-w-md" data-testid="admin-add-user-dialog">
        <DialogHeader>
          <DialogTitle>Tambah Pengguna</DialogTitle>
          <DialogDescription>Email harus termasuk daftar domain yang diizinkan.</DialogDescription>
        </DialogHeader>
        <Field label="Email">
          <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="nama@thrive.co.id" className="h-10 bg-[hsl(220_12%_8%)]" data-testid="admin-add-user-email-input" />
        </Field>
        <Field label="Nama (opsional)">
          <Input value={name} onChange={(e) => setName(e.target.value)} className="h-10 bg-[hsl(220_12%_8%)]" data-testid="admin-add-user-name-input" />
        </Field>
        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>Batal</Button>
          <Button onClick={save} disabled={saving} data-testid="admin-add-user-save">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />} Simpan
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditLimitDialog({ user, onOpenChange, onDone, defaultLimit }) {
  const [limit, setLimit] = useState(0);
  const [useDefault, setUseDefault] = useState(true);
  const [status, setStatus] = useState("active");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (user) {
      setLimit(user.token_limit ?? user.effective_limit);
      setUseDefault(user.token_limit === null || user.token_limit === undefined);
      setStatus(user.status || "active");
    }
  }, [user]);
  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/users/${encodeURIComponent(user.email)}`, {
        token_limit: useDefault ? null : Math.round(limit),
        use_default_limit: useDefault,
        status,
      });
      toast.success("Diperbarui");
      onOpenChange();
      onDone && onDone();
    } catch (e) {
      toast.error(errMsg(e, "Gagal menyimpan"));
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open={!!user} onOpenChange={(v) => !v && onOpenChange()}>
      <DialogContent className="bg-card border-border sm:max-w-md" data-testid="admin-edit-limit-dialog">
        <DialogHeader>
          <DialogTitle>Kuota & Status</DialogTitle>
          <DialogDescription>{user?.email}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <SwitchRow
            label="Pakai kuota default"
            hint={`Default saat ini ${fmtNum(defaultLimit)} token/bulan.`}
            checked={useDefault}
            onChange={setUseDefault}
            testid="admin-edit-use-default-switch"
          />
          <Field label="Batas token bulanan" hint="Minimum 0, maksimum 100.000.000.">
            <NumInput value={limit} onChange={setLimit} suffix="token" decimals={0} disabled={useDefault} testid="admin-edit-limit-input" />
          </Field>
          {user?.role !== "admin" && (
            <Field label="Status akun">
              <Segmented
                value={status}
                onChange={setStatus}
                options={[
                  { value: "active", label: "Aktif" },
                  { value: "inactive", label: "Nonaktif" },
                ]}
                size="sm"
                testid="admin-edit-status"
              />
            </Field>
          )}
        </div>
        <DialogFooter>
          <Button variant="secondary" onClick={onOpenChange}>Batal</Button>
          <Button onClick={save} disabled={saving} data-testid="admin-edit-limit-save">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />} Simpan
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ------------------------- Email / WA / AI / Security ------------------------- */
function SettingsPanel({ section }) {
  const { user } = useAuth();
  const [settings, setSettings] = useState(null);
  const load = () =>
    api
      .get("/admin/settings")
      .then(({ data }) => setSettings(data))
      .catch((e) => toast.error(errMsg(e, "Gagal memuat setting")));
  useEffect(() => {
    load();
  }, []);
  if (!settings) return <Skeleton className="h-96 bg-[hsl(220_10%_11%)]" />;
  if (section === "email") return <EmailSection settings={settings} onReload={load} />;
  if (section === "whatsapp") return <WhatsAppSection settings={settings} onReload={load} />;
  if (section === "ai") return <AiSection settings={settings} onReload={load} />;
  if (section === "scraping") return <ScrapingSection settings={settings} onReload={load} />;
  return <SecuritySection settings={settings} onReload={load} />;
}

function EmailSection({ settings, onReload }) {
  const [form, setForm] = useState(settings.email);
  const [smtpPassword, setSmtpPassword] = useState("");
  const [resendKey, setResendKey] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testTo, setTestTo] = useState("");

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        provider: form.provider,
        smtp: {
          host: form.smtp.host,
          port: form.smtp.port,
          username: form.smtp.username,
          password: smtpPassword || null,
          security: form.smtp.security,
          from_email: form.smtp.from_email,
          from_name: form.smtp.from_name,
        },
        resend: {
          api_key: resendKey || null,
          from_email: form.resend.from_email,
          from_name: form.resend.from_name,
        },
      };
      await api.put("/admin/settings/email", body);
      toast.success("Pengaturan email disimpan");
      setSmtpPassword("");
      setResendKey("");
      onReload();
    } catch (e) {
      toast.error(errMsg(e, "Gagal menyimpan"));
    } finally {
      setSaving(false);
    }
  };
  const test = async () => {
    setTesting(true);
    try {
      const payload = testTo.trim() ? { to: testTo.trim().toLowerCase() } : {};
      const { data } = await api.post("/admin/test/email", payload);
      toast.success(`Email tes terkirim (via ${data.provider}) ke ${data.sent_to}`);
    } catch (e) {
      toast.error(errMsg(e, "Gagal mengirim email tes"));
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="grid lg:grid-cols-[1fr_420px] gap-5">
      <SectionCard title="Pengirim Email" description="OTP login dikirim ke semua pengguna melalui provider ini." testid="admin-email-section">
        <div className="space-y-4">
          <Field label="Provider">
            <Segmented
              value={form.provider}
              onChange={(v) => setForm({ ...form, provider: v })}
              testid="admin-email-provider"
              options={[
                { value: "emergent", label: "Emergent Managed (default)" },
                { value: "smtp", label: "SMTP sendiri" },
                { value: "resend", label: "Resend.com" },
              ]}
            />
          </Field>

          {form.provider === "emergent" && (
            <InfoNote>
              Pakai layanan email yang sudah dikonfigurasi Emergent. Tidak perlu API key. Nama pengirim: <b>{settings.email.managed_from_name}</b>.
            </InfoNote>
          )}

          {form.provider === "smtp" && (
            <div className="flex flex-wrap items-center gap-2 rounded-lg border border-[color:var(--hairline)] bg-[hsl(43_30%_7%)] px-3 py-2.5" data-testid="admin-smtp-gmail-preset-box">
              <span className="text-xs text-[hsl(45_20%_82%)] flex-1 min-w-[200px]">Rekomendasi: <b>SMTP Gmail / Google Workspace</b> (smtp.gmail.com · 587 · STARTTLS) dengan <b>App Password</b>.</span>
              <Button
                size="sm"
                variant="outline"
                onClick={() =>
                  setForm({
                    ...form,
                    smtp: {
                      ...form.smtp,
                      host: "smtp.gmail.com",
                      port: 587,
                      security: "starttls",
                      from_email: form.smtp.from_email || form.smtp.username || "",
                      from_name: form.smtp.from_name || "Kalkulator Eddy",
                    },
                  })
                }
                data-testid="admin-smtp-gmail-preset"
              >
                <Mail className="h-4 w-4 mr-1.5" /> Isi preset Gmail
              </Button>
            </div>
          )}

          {form.provider === "smtp" && (
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="SMTP host">
                <TextInput value={form.smtp.host} onChange={(v) => setForm({ ...form, smtp: { ...form.smtp, host: v } })} testid="admin-smtp-host" />
              </Field>
              <Field label="Port">
                <NumInput value={form.smtp.port} onChange={(v) => setForm({ ...form, smtp: { ...form.smtp, port: v } })} decimals={0} testid="admin-smtp-port" />
              </Field>
              <Field label="Username">
                <TextInput value={form.smtp.username} onChange={(v) => setForm({ ...form, smtp: { ...form.smtp, username: v } })} testid="admin-smtp-username" />
              </Field>
              <Field label={`Password ${form.smtp.password_set ? "(tersimpan)" : ""}`}>
                <Input type="password" value={smtpPassword} onChange={(e) => setSmtpPassword(e.target.value)} placeholder={form.smtp.password_set ? "Biarkan kosong untuk tetap pakai yang lama" : ""} className="h-10 bg-[hsl(220_12%_8%)]" data-testid="admin-smtp-password" />
              </Field>
              <Field label="Keamanan">
                <Sel
                  value={form.smtp.security}
                  onChange={(v) => setForm({ ...form, smtp: { ...form.smtp, security: v } })}
                  options={[
                    { value: "starttls", label: "STARTTLS (port 587)" },
                    { value: "ssl", label: "SSL/TLS (port 465)" },
                    { value: "none", label: "None (dev only)" },
                  ]}
                  testid="admin-smtp-security"
                />
              </Field>
              <Field label="From email">
                <TextInput value={form.smtp.from_email} onChange={(v) => setForm({ ...form, smtp: { ...form.smtp, from_email: v } })} testid="admin-smtp-from-email" />
              </Field>
              <Field label="From name" className="sm:col-span-2">
                <TextInput value={form.smtp.from_name} onChange={(v) => setForm({ ...form, smtp: { ...form.smtp, from_name: v } })} testid="admin-smtp-from-name" />
              </Field>
            </div>
          )}

          {form.provider === "resend" && (
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label={`API key ${form.resend.api_key_set ? `(tersimpan …${form.resend.api_key_last4})` : ""}`} className="sm:col-span-2">
                <Input type="password" value={resendKey} onChange={(e) => setResendKey(e.target.value)} placeholder="re_xxxxxx…" className="h-10 bg-[hsl(220_12%_8%)]" data-testid="admin-resend-key" />
              </Field>
              <Field label="From email">
                <TextInput value={form.resend.from_email} onChange={(v) => setForm({ ...form, resend: { ...form.resend, from_email: v } })} testid="admin-resend-from-email" />
              </Field>
              <Field label="From name">
                <TextInput value={form.resend.from_name} onChange={(v) => setForm({ ...form, resend: { ...form.resend, from_name: v } })} testid="admin-resend-from-name" />
              </Field>
            </div>
          )}

          <div className="flex flex-wrap items-center gap-2 pt-2">
            <Button onClick={save} disabled={saving} data-testid="admin-email-save">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} Simpan
            </Button>
          </div>

          <div className="rounded-lg border border-[color:var(--hairline)] bg-[hsl(43_30%_7%)] p-3 space-y-2" data-testid="admin-email-test-box">
            <div className="text-xs text-[hsl(45_20%_82%)]">
              Uji pengiriman. Kosongkan untuk mengirim ke alamat Anda, atau isi untuk menguji ke alamat lain (misal: pengguna <b>@thrive.co.id</b> yang mengeluh tidak menerima OTP).
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Input
                type="email"
                value={testTo}
                onChange={(e) => setTestTo(e.target.value)}
                placeholder="(kosong = ke email admin Anda) · atau nama@thrive.co.id"
                className="h-10 bg-[hsl(220_12%_8%)] flex-1 min-w-[240px]"
                data-testid="admin-email-test-to"
              />
              <Button variant="secondary" onClick={test} disabled={testing} className="border border-border" data-testid="admin-email-test">
                {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                {testTo.trim() ? " Kirim tes ke alamat ini" : " Kirim email tes ke saya"}
              </Button>
            </div>
          </div>
        </div>
      </SectionCard>
      <SectionCard title="Petunjuk" testid="admin-email-help">
        <div className="space-y-3 text-xs text-[hsl(45_15%_82%)] leading-relaxed">
          <p>
            Email Emergent Managed sudah siap pakai; cocok untuk MVP. Untuk produksi, pakai SMTP perusahaan atau Resend dengan domain terverifikasi.
          </p>
          <p>
            Pastikan DNS SPF, DKIM dan DMARC domain pengirim valid agar email OTP masuk ke inbox.
          </p>
          <InfoNote>
            Tombol <b>Kirim email tes</b> mengirim contoh OTP ke akun admin ({settings.updated_by || "-"} / Anda).
          </InfoNote>
          <div className="rounded-lg border border-border bg-[hsl(220_10%_7%)] p-3 space-y-2" data-testid="admin-smtp-gmail-guide">
            <div className="text-sm font-semibold text-foreground">Panduan SMTP Gmail (direkomendasikan)</div>
            <ol className="list-decimal pl-4 space-y-1.5">
              <li>Login ke akun Gmail / Google Workspace pengirim (mis. <b>noreply@thrive.co.id</b>).</li>
              <li>Aktifkan <b>Verifikasi 2 Langkah</b>: myaccount.google.com → Keamanan → Verifikasi 2 Langkah.</li>
              <li>Buat <b>App Password</b>: myaccount.google.com/apppasswords → nama aplikasi "Kalkulator Eddy" → salin 16 karakter.</li>
              <li>Di sini pilih <b>SMTP sendiri</b> → klik <b>Isi preset Gmail</b>.</li>
              <li>Username = alamat Gmail lengkap, Password = App Password (tanpa spasi), From email = alamat yang sama.</li>
              <li>Klik <b>Simpan</b>, lalu <b>Kirim email tes ke saya</b>.</li>
            </ol>
            <div className="grid grid-cols-2 gap-x-3 gap-y-1 pt-1 num text-[11px]">
              <span className="text-muted-foreground">Host</span><span>smtp.gmail.com</span>
              <span className="text-muted-foreground">Port</span><span>587 (STARTTLS) / 465 (SSL)</span>
              <span className="text-muted-foreground">Batas kirim</span><span>Gmail ±500/hari · Workspace ±2.000/hari</span>
            </div>
            <p className="text-muted-foreground">
              Google Workspace dengan domain <b>thrive.co.id</b> paling disarankan: email OTP dikirim dari domain sendiri sehingga tidak ditolak dan jarang masuk spam. Jangan pakai password Gmail biasa — Google menolaknya; wajib App Password.
            </p>
          </div>
        </div>
      </SectionCard>
    </div>
  );
}

function WhatsAppSection({ settings, onReload }) {
  const [form, setForm] = useState(settings.whatsapp);
  const [token, setToken] = useState("");
  const [phone, setPhone] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings/whatsapp", {
        enabled: form.enabled,
        provider: form.provider,
        api_url: form.api_url,
        sender: form.sender,
        token: token || null,
      });
      toast.success("Setting WhatsApp disimpan");
      setToken("");
      onReload();
    } catch (e) {
      toast.error(errMsg(e, "Gagal menyimpan"));
    } finally {
      setSaving(false);
    }
  };
  const test = async () => {
    if (!phone) return toast.error("Isi nomor WhatsApp dulu");
    setTesting(true);
    try {
      await api.post("/admin/test/whatsapp", { phone });
      toast.success(`Pesan tes dikirim ke ${phone}`);
    } catch (e) {
      toast.error(errMsg(e, "Gagal mengirim WhatsApp"));
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="grid lg:grid-cols-[1fr_420px] gap-5">
      <SectionCard title="WhatsApp Admin" description="Integrasi WhatsApp disediakan sebagai opsi, saat ini dinonaktifkan." testid="admin-whatsapp-section">
        <div className="space-y-4">
          <SwitchRow
            label="Aktifkan integrasi WhatsApp"
            hint="Jika nonaktif, tombol kirim WhatsApp disembunyikan dari aplikasi."
            checked={form.enabled}
            onChange={(v) => setForm({ ...form, enabled: v })}
            testid="admin-whatsapp-enabled-switch"
          />
          <div className={form.enabled ? "space-y-4" : "space-y-4 opacity-60 pointer-events-none"}>
            <Field label="Provider">
              <Segmented
                value={form.provider}
                onChange={(v) => setForm({ ...form, provider: v })}
                testid="admin-whatsapp-provider"
                size="sm"
                options={[
                  { value: "fonnte", label: "Fonnte.com" },
                  { value: "wablas", label: "Wablas" },
                  { value: "custom", label: "API Custom" },
                ]}
              />
            </Field>
            <Field label="API URL">
              <TextInput value={form.api_url} onChange={(v) => setForm({ ...form, api_url: v })} testid="admin-whatsapp-api-url" />
            </Field>
            <Field label={`Token ${form.token_set ? `(tersimpan …${form.token_last4})` : ""}`}>
              <Input type="password" value={token} onChange={(e) => setToken(e.target.value)} placeholder={form.token_set ? "Biarkan kosong untuk tetap pakai yang lama" : ""} className="h-10 bg-[hsl(220_12%_8%)]" data-testid="admin-whatsapp-token" />
            </Field>
            <Field label="Sender (nomor pengirim)">
              <TextInput value={form.sender} onChange={(v) => setForm({ ...form, sender: v })} placeholder="628xx…" testid="admin-whatsapp-sender" />
            </Field>
          </div>
          <div className="flex items-center gap-2 pt-2">
            <Button onClick={save} disabled={saving} data-testid="admin-whatsapp-save">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} Simpan
            </Button>
            {form.enabled && (
              <div className="flex gap-2 flex-1">
                <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="628…" className="h-9 bg-[hsl(220_12%_8%)] max-w-[220px]" data-testid="admin-whatsapp-test-phone" />
                <Button variant="secondary" onClick={test} disabled={testing} className="border border-border" data-testid="admin-whatsapp-test">
                  {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Kirim tes
                </Button>
              </div>
            )}
          </div>
        </div>
      </SectionCard>
      <SectionCard title="Catatan" testid="admin-whatsapp-help">
        <div className="space-y-3 text-xs text-[hsl(45_15%_82%)] leading-relaxed">
          <InfoNote>
            MVP saat ini menggunakan OTP email. WhatsApp OTP akan diaktifkan setelah provider & kredensial siap. Integrasi ini hanya mempengaruhi tombol WhatsApp admin, bukan alur login.
          </InfoNote>
        </div>
      </SectionCard>
    </div>
  );
}

function AiSection({ settings, onReload }) {
  const [form, setForm] = useState(settings.ai);
  const [openaiKey, setOpenaiKey] = useState("");
  const [clearKey, setClearKey] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings/ai", {
        model: form.model,
        key_source: form.key_source,
        openai_key: openaiKey || null,
        clear_openai_key: clearKey,
        default_monthly_limit: Math.round(form.default_monthly_limit),
      });
      toast.success("Setting AI disimpan");
      setOpenaiKey("");
      setClearKey(false);
      onReload();
    } catch (e) {
      toast.error(errMsg(e, "Gagal menyimpan"));
    } finally {
      setSaving(false);
    }
  };
  const test = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/admin/test/ai");
      toast.success(`AI OK · ${data.total_tokens} token · ${data.model}`);
    } catch (e) {
      toast.error(errMsg(e, "Gagal tes AI"));
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="grid lg:grid-cols-[1fr_420px] gap-5">
      <SectionCard title="Konfigurasi AI Recommendation" description="Model AI dan kuota default untuk semua tool." testid="admin-ai-section">
        <div className="space-y-4">
          <Field label="Model AI">
            <Sel
              value={form.model}
              onChange={(v) => setForm({ ...form, model: v })}
              options={(form.available_models || []).map((m) => ({ value: m, label: m }))}
              testid="admin-ai-model"
            />
          </Field>
          <Field label="Sumber API key">
            <Segmented
              value={form.key_source}
              onChange={(v) => setForm({ ...form, key_source: v })}
              testid="admin-ai-key-source"
              size="sm"
              options={[
                { value: "emergent", label: "Emergent Universal (default)" },
                { value: "own", label: "OpenAI API key sendiri" },
              ]}
            />
          </Field>
          {form.key_source === "own" && (
            <div className="grid sm:grid-cols-[1fr_auto] gap-3 items-end">
              <Field label={`OpenAI API key ${form.openai_key_set ? `(tersimpan …${form.openai_key_last4})` : ""}`}>
                <Input type="password" value={openaiKey} onChange={(e) => setOpenaiKey(e.target.value)} placeholder="sk-…" className="h-10 bg-[hsl(220_12%_8%)]" data-testid="admin-ai-openai-key" />
              </Field>
              {form.openai_key_set && (
                <Button variant="ghost" onClick={() => setClearKey((v) => !v)} className={clearKey ? "text-[hsl(0_72%_64%)]" : "text-muted-foreground"} data-testid="admin-ai-clear-key">
                  {clearKey ? "Batalkan hapus" : "Hapus key tersimpan"}
                </Button>
              )}
            </div>
          )}
          <Field label="Kuota default per pengguna / bulan" hint="Tombol AI akan menolak request jika pemakaian melewati batas ini.">
            <NumInput value={form.default_monthly_limit} onChange={(v) => setForm({ ...form, default_monthly_limit: v })} suffix="token" decimals={0} testid="admin-ai-default-limit" />
          </Field>
          <div className="flex items-center gap-2 pt-2">
            <Button onClick={save} disabled={saving} data-testid="admin-ai-save">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} Simpan
            </Button>
            <Button variant="secondary" onClick={test} disabled={testing} className="border border-border" data-testid="admin-ai-test">
              {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Tes koneksi AI
            </Button>
          </div>
        </div>
      </SectionCard>
      <SectionCard title="Panduan" testid="admin-ai-help">
        <div className="space-y-3 text-xs text-[hsl(45_15%_82%)] leading-relaxed">
          <p>
            <b>Emergent Universal</b> adalah kunci yang sudah termasuk langganan Emergent; mendukung model GPT seri 5. Pilih <b>OpenAI sendiri</b> jika ingin kontrol penagihan ke akun OpenAI Anda.
          </p>
          <p>
            Default kuota adalah <b>500.000 token/pengguna/bulan</b>. Admin bisa menaikkan kuota khusus per pengguna di tab Pengguna.
          </p>
          <InfoNote>
            Setiap tombol <b>Rekomendasi AI</b> yang ditekan user akan mengurangi kuota mereka. Pemakaian direset otomatis setiap bulan.
          </InfoNote>
        </div>
      </SectionCard>
    </div>
  );
}

function ScrapingSection({ settings, onReload }) {
  const scr = settings.scraper || { provider_default: "apollo", apollo: {}, rapidapi: {} };
  const [providerDefault, setProviderDefault] = useState(scr.provider_default || "apollo");
  const [apolloKey, setApolloKey] = useState("");
  const [rapidKey, setRapidKey] = useState("");
  const [host, setHost] = useState(scr.rapidapi?.host || "linkedin-api8.p.rapidapi.com");
  const [peoplePath, setPeoplePath] = useState(scr.rapidapi?.people_path || "/search-people");
  const [saving, setSaving] = useState(false);

  const save = async ({ clearApollo = false, clearRapid = false } = {}) => {
    setSaving(true);
    try {
      await api.put("/admin/settings/scraper", {
        provider_default: providerDefault,
        apollo: { api_key: apolloKey || null, clear_api_key: clearApollo },
        rapidapi: {
          api_key: rapidKey || null,
          clear_api_key: clearRapid,
          host: host || "linkedin-api8.p.rapidapi.com",
          people_path: peoplePath || "/search-people",
        },
      });
      toast.success("Pengaturan scraping disimpan");
      setApolloKey("");
      setRapidKey("");
      onReload();
    } catch (e) {
      toast.error(errMsg(e, "Gagal menyimpan"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="grid lg:grid-cols-[1fr_380px] gap-5">
      <SectionCard
        title="Scraping Kontak LinkedIn"
        description="Pilih sumber data kontak (Apollo.io untuk kualitas terbaik, RapidAPI untuk biaya lebih rendah). Kunci API tersimpan terenkripsi di server."
        testid="admin-scraping-section"
      >
        <div className="space-y-5">
          <Field label="Sumber data default">
            <Segmented
              value={providerDefault}
              onChange={setProviderDefault}
              testid="admin-scraping-provider-default"
              options={[
                { value: "apollo", label: "Apollo.io" },
                { value: "rapidapi", label: "RapidAPI LinkedIn" },
              ]}
            />
          </Field>

          <div className="rounded-xl border border-border bg-[hsl(220_10%_7%)] p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <KeyRound className="h-4 w-4 text-primary" />
                <span className="text-sm font-medium">Apollo.io API Key</span>
                {scr.apollo?.api_key_set && (
                  <Badge variant="outline" className="border-primary/40 text-primary">
                    tersimpan ••••{scr.apollo.api_key_last4}
                  </Badge>
                )}
              </div>
              {scr.apollo?.api_key_set && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => save({ clearApollo: true })}
                  disabled={saving}
                  data-testid="admin-scraping-clear-apollo"
                >
                  Hapus
                </Button>
              )}
            </div>
            <Input
              type="password"
              placeholder={scr.apollo?.api_key_set ? "Biarkan kosong untuk tetap pakai yang lama" : "Tempel API key Apollo…"}
              value={apolloKey}
              onChange={(e) => setApolloKey(e.target.value)}
              className="h-10 bg-[hsl(220_12%_8%)]"
              data-testid="admin-scraping-apollo-key-input"
            />
            <p className="text-[11px] text-muted-foreground leading-relaxed">
              Dapat di Apollo → Settings → Integrations → API Keys. Database 270jt+ kontak B2B dengan email terverifikasi; tersedia tombol <b>Reveal</b> untuk membuka email/telepon saat dibutuhkan.
            </p>
          </div>

          <div className="rounded-xl border border-border bg-[hsl(220_10%_7%)] p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <KeyRound className="h-4 w-4 text-primary" />
                <span className="text-sm font-medium">RapidAPI Key (LinkedIn Scraper)</span>
                {scr.rapidapi?.api_key_set && (
                  <Badge variant="outline" className="border-primary/40 text-primary">
                    tersimpan ••••{scr.rapidapi.api_key_last4}
                  </Badge>
                )}
              </div>
              {scr.rapidapi?.api_key_set && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => save({ clearRapid: true })}
                  disabled={saving}
                  data-testid="admin-scraping-clear-rapidapi"
                >
                  Hapus
                </Button>
              )}
            </div>
            <Input
              type="password"
              placeholder={scr.rapidapi?.api_key_set ? "Biarkan kosong untuk tetap pakai yang lama" : "Tempel RapidAPI key…"}
              value={rapidKey}
              onChange={(e) => setRapidKey(e.target.value)}
              className="h-10 bg-[hsl(220_12%_8%)]"
              data-testid="admin-scraping-rapidapi-key-input"
            />
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Host RapidAPI">
                <Input
                  value={host}
                  onChange={(e) => setHost(e.target.value)}
                  placeholder="linkedin-api8.p.rapidapi.com"
                  className="h-10 bg-[hsl(220_12%_8%)]"
                  data-testid="admin-scraping-rapidapi-host"
                />
              </Field>
              <Field label="People search path">
                <Input
                  value={peoplePath}
                  onChange={(e) => setPeoplePath(e.target.value)}
                  placeholder="/search-people"
                  className="h-10 bg-[hsl(220_12%_8%)]"
                  data-testid="admin-scraping-rapidapi-path"
                />
              </Field>
            </div>
            <p className="text-[11px] text-muted-foreground leading-relaxed">
              Berlangganan di RapidAPI → provider LinkedIn Scraper (contoh default: <b>linkedin-api8</b>). Alternatif lain: isi host & path sesuai provider lain.
            </p>
          </div>

          <div className="flex items-center gap-2 pt-1">
            <Button onClick={() => save()} disabled={saving} className="press" data-testid="admin-scraping-save">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Simpan pengaturan
            </Button>
          </div>
        </div>
      </SectionCard>

      <SectionCard title="Catatan" description="Kebijakan dan batasan penggunaan." testid="admin-scraping-notes">
        <ul className="text-xs text-muted-foreground space-y-2 leading-relaxed list-disc pl-4">
          <li><b className="text-foreground">Legal:</b> scraping langsung LinkedIn melanggar TOS mereka; gunakan provider resmi di atas.</li>
          <li><b className="text-foreground">Akses:</b> fitur ini hanya untuk Admin ({"eddy.yansen@gmail.com"}).</li>
          <li><b className="text-foreground">Email/WA tersedia:</b> ditandai badge pada setiap baris hasil pencarian.</li>
          <li><b className="text-foreground">Apollo Reveal:</b> membuka email/telepon memakai credit Apollo. Gunakan hanya saat diperlukan.</li>
          <li><b className="text-foreground">Ekspor:</b> hasil pencarian bisa diunduh dalam format CSV maupun XLSX.</li>
        </ul>
      </SectionCard>
    </div>
  );
}

function SecuritySection({ settings, onReload }) {
  const [emails, setEmails] = useState(settings.access.allowed_emails.join("\n"));
  const [domains, setDomains] = useState(settings.access.allowed_domains.join("\n"));
  const [saving, setSaving] = useState(false);

  const [currentPin, setCurrentPin] = useState("");
  const [newPin, setNewPin] = useState("");
  const [savingPin, setSavingPin] = useState(false);

  const saveAccess = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings/access", {
        allowed_emails: emails.split(/[\n,;\s]+/).filter(Boolean),
        allowed_domains: domains.split(/[\n,;\s]+/).filter(Boolean),
      });
      toast.success("Daftar akses disimpan");
      onReload();
    } catch (e) {
      toast.error(errMsg(e, "Gagal menyimpan"));
    } finally {
      setSaving(false);
    }
  };

  const savePin = async () => {
    if (!/^\d{4}$/.test(newPin)) return toast.error("PIN baru harus 4 digit angka");
    setSavingPin(true);
    try {
      await api.put("/admin/pin", { current_pin: currentPin, new_pin: newPin });
      toast.success("PIN admin diperbarui");
      setCurrentPin("");
      setNewPin("");
    } catch (e) {
      toast.error(errMsg(e, "Gagal mengubah PIN"));
    } finally {
      setSavingPin(false);
    }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-5">
      <SectionCard title="Daftar Akses" description="Hanya email yang cocok dengan daftar ini yang boleh sign-in." testid="admin-access-section">
        <div className="space-y-4">
          <Field label="Email khusus yang diizinkan (satu per baris)">
            <Textarea
              value={emails}
              onChange={(e) => setEmails(e.target.value)}
              placeholder="eddy.yansen@gmail.com"
              className="min-h-[100px] bg-[hsl(220_12%_8%)] border-border text-sm font-mono"
              data-testid="admin-access-emails"
            />
          </Field>
          <Field label="Domain yang diizinkan (satu per baris)" hint="Contoh: brightcorporation (semua TLD seperti .com/.co.id otomatis dicocokkan).">
            <Textarea
              value={domains}
              onChange={(e) => setDomains(e.target.value)}
              placeholder="thrive.co.id&#10;brightcorporation"
              className="min-h-[100px] bg-[hsl(220_12%_8%)] border-border text-sm font-mono"
              data-testid="admin-access-domains"
            />
          </Field>
          <Button onClick={saveAccess} disabled={saving} data-testid="admin-access-save">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />} Simpan daftar akses
          </Button>
        </div>
      </SectionCard>
      <SectionCard title="PIN Admin" description="PIN login khusus admin (eddy.yansen@gmail.com)." testid="admin-pin-section">
        <div className="space-y-4">
          <Field label="PIN saat ini">
            <Input type="password" value={currentPin} onChange={(e) => setCurrentPin(e.target.value.replace(/\D/g, "").slice(0, 8))} className="h-10 bg-[hsl(220_12%_8%)] num" inputMode="numeric" data-testid="admin-current-pin" />
          </Field>
          <Field label="PIN baru (4 digit)">
            <Input type="password" value={newPin} onChange={(e) => setNewPin(e.target.value.replace(/\D/g, "").slice(0, 4))} className="h-10 bg-[hsl(220_12%_8%)] num" inputMode="numeric" data-testid="admin-new-pin" />
          </Field>
          <Button onClick={savePin} disabled={savingPin || !currentPin || newPin.length !== 4} data-testid="admin-save-pin">
            {savingPin && <Loader2 className="h-4 w-4 animate-spin" />} Ubah PIN admin
          </Button>
          <InfoNote>
            Jika lupa PIN, admin bisa kembali ke PIN default <b>1689</b> dengan mengubah variable <code>ADMIN_DEFAULT_PIN</code> dan restart backend.
          </InfoNote>
        </div>
      </SectionCard>
    </div>
  );
}

/* ------------------------- Inventory (XLSX re-import) ------------------------- */
function InventoryPanel() {
  const [servers, setServers] = useState(null);
  const [replace, setReplace] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);

  const load = () =>
    api
      .get("/servers")
      .then(({ data }) => setServers(data))
      .catch(() => setServers([]));
  useEffect(() => {
    load();
  }, []);

  const upload = async (file) => {
    if (!file) return;
    const form = new FormData();
    form.append("file", file);
    form.append("replace", replace ? "true" : "false");
    setUploading(true);
    setResult(null);
    try {
      const { data } = await api.post("/admin/inventory/import", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      toast.success(`Berhasil import ${data.servers} server & ${data.vms} VM`);
      load();
    } catch (e) {
      toast.error(errMsg(e, "Gagal import"));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const totalVm = (servers || []).reduce((a, s) => a + (s.allocated?.count || 0), 0);

  return (
    <div className="space-y-5">
      <div className="grid sm:grid-cols-3 gap-3">
        <KpiCard label="Server terdaftar" value={servers === null ? "–" : fmtNum((servers || []).length)} testid="admin-inventory-kpi-servers" />
        <KpiCard label="VM running" value={servers === null ? "–" : fmtNum(totalVm)} testid="admin-inventory-kpi-vms" />
        <KpiCard label="Sumber data" value="Sheet + Manual" sub="Hasil gabungan seed & input manual" testid="admin-inventory-kpi-sources" />
      </div>
      <SectionCard
        title="Re-import Inventory dari Google Sheet / XLSX"
        description="Upload file XLSX (unduh dari Google Sheets: File → Download → Microsoft Excel). Pilih mode penggabungan: tambah/update (default) atau ganti seluruh data sheet."
        testid="admin-inventory-import-card"
      >
        <div className="space-y-4">
          <SwitchRow
            label="Ganti seluruh data hasil sheet sebelumnya"
            hint="Jika aktif: server & VM dengan source=sheet akan dihapus lalu diganti dengan isi file baru. Server/VM yang ditambah manual tidak terpengaruh."
            checked={replace}
            onChange={setReplace}
            testid="admin-inventory-replace-switch"
          />
          <div className="flex flex-wrap items-center gap-3">
            <input
              ref={fileRef}
              type="file"
              accept=".xlsx"
              onChange={(e) => upload(e.target.files?.[0])}
              className="hidden"
              data-testid="admin-inventory-file-input"
            />
            <Button
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              data-testid="admin-inventory-upload-button"
            >
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Pilih file XLSX
            </Button>
            <span className="text-xs text-muted-foreground">
              File harus memiliki sheet <b className="text-foreground">Hardware</b> dan <b className="text-foreground">VM</b> sesuai template.
            </span>
          </div>

          {result && (
            <div className="rounded-lg border border-[color:var(--hairline)] bg-[hsl(43_30%_7%)] p-4" data-testid="admin-inventory-result">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle2 className="h-4 w-4 text-[hsl(160_55%_60%)]" />
                <span className="font-medium">Import selesai</span>
              </div>
              <Row label="Server ditambahkan / diupdate" value={fmtNum(result.servers)} />
              <Row label="VM ditambahkan / diupdate" value={fmtNum(result.vms)} />
              {result.replaced !== undefined && <Row label="Record sheet lama dihapus" value={fmtNum(result.replaced)} muted />}
              {result.warnings?.length > 0 && (
                <div className="mt-2 text-xs text-[hsl(38_90%_80%)]">
                  <div className="flex items-center gap-1 mb-1">
                    <AlertTriangle className="h-3.5 w-3.5" /> Peringatan:
                  </div>
                  <ul className="list-disc pl-5 space-y-0.5">
                    {result.warnings.map((w, i) => (
                      <li key={i}>{w}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          <InfoNote>
            Template yang diharapkan: sheet <b>Hardware</b> dengan kolom <i>Hostname, Lokasi, IP, CPU, Total Core / Thread, RAM, Disk OS, Disk Data, Total Disk Data, Network, Jumlah VM Running</i>. Sheet <b>VM</b> dengan kolom <i>Host, VMID, Nama, Tipe, Status, vCPU, RAM, Disk</i>.
          </InfoNote>
        </div>
      </SectionCard>

      <SectionCard title="Ringkasan server" bodyClassName="p-0" testid="admin-inventory-list">
        <div className="overflow-x-auto scroll-gold">
          <table className="dense-table w-full text-xs">
            <thead>
              <tr className="bg-secondary">
                <th className="text-left px-5 py-3">Hostname</th>
                <th className="text-left px-3 py-3">Lokasi</th>
                <th className="text-left px-3 py-3">Role</th>
                <th className="text-right px-3 py-3">Core/Thread</th>
                <th className="text-right px-3 py-3">RAM (GB)</th>
                <th className="text-right px-3 py-3">Disk (TB)</th>
                <th className="text-right px-3 py-3">VM</th>
                <th className="text-left px-3 py-3">Sumber</th>
                <th className="text-left px-5 py-3">Terakhir update</th>
              </tr>
            </thead>
            <tbody>
              {(servers || []).map((s) => (
                <tr key={s.id} className="border-t border-border hover:bg-[hsl(220_10%_9%)]">
                  <td className="px-5 py-2.5 font-medium">{s.hostname}</td>
                  <td className="px-3 py-2.5 text-muted-foreground">{s.location}</td>
                  <td className="px-3 py-2.5">
                    <Badge variant="outline" className="border-border text-[10px] text-muted-foreground">{s.role}</Badge>
                  </td>
                  <td className="px-3 py-2.5 text-right num">{s.cores}/{s.threads}</td>
                  <td className="px-3 py-2.5 text-right num">{fmtNum(s.ram_gb)}</td>
                  <td className="px-3 py-2.5 text-right num">{fmtNum(s.storage_tb)}</td>
                  <td className="px-3 py-2.5 text-right num text-primary">{s.allocated?.count || 0}</td>
                  <td className="px-3 py-2.5">
                    <Badge variant="outline" className={`border-border text-[10px] ${s.source === "sheet" ? "text-[hsl(43_60%_66%)]" : "text-muted-foreground"}`}>
                      {s.source}
                    </Badge>
                  </td>
                  <td className="px-5 py-2.5 text-[11px] text-muted-foreground">{fmtDate(s.updated_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </SectionCard>
    </div>
  );
}
